import { PreparedItemRequest } from '@/models/item-request.models';
import { useOrganizationStore } from '@/store/organizationStore';
import dayjs from '@/utils/dayjs';
import { useIntl } from './internationalization';
import { computed, nextTick, ref } from 'vue';
import { organizationData } from '@/store/helpers';
import { EditorValue } from '@/types/editor';
import { useCustomFields } from '@/utils/custom-fields';
import { useRoot } from '@/utils/component';
import { cloneDeep, isEqual } from 'lodash-es';
import {
  Colors,
  CustomField,
  CustomFields,
  CustomFieldType,
  ItemRequest,
  Priority,
  Relationship,
  SubmitItemRequest,
} from '@/api';

export function prepareItemRequests(
  itemRequests: Array<ItemRequest>
): Array<PreparedItemRequest> {
  const organizationStore = useOrganizationStore();
  const { dateTimeFormat } = useIntl();
  const workTypes = organizationStore.workTypes;

  return itemRequests
    .map((itemRequest) => {
      const workType = workTypes.find(
        (workType) => workType.id === itemRequest.workTypeId
      );
      if (!workType) {
        console.warn(
          `Work type with id ${itemRequest.workTypeId} not found for item request with id ${itemRequest.id}. This item request will be filtered out.`
        );
        return null;
      }
      return {
        ...itemRequest,
        dateAdded: dayjs(itemRequest.dateAdded).format(
          dateTimeFormat.value.date
        ),
        workType: workType,
      };
    })
    .filter((item): item is PreparedItemRequest => item !== null);
}

export function useItemRequestForm() {
  const { itemRequests, priorities } = organizationData();
  const root = useRoot();

  const selectedRequestId = ref<ItemRequest['id'] | null>(null);
  const selectedPublicRequest = ref<ItemRequest | null>(null);

  const selectedRequest = computed(() => {
    const sr = itemRequests.value.find(
      (itemRequest) => itemRequest.id === selectedRequestId.value
    );
    if (sr) return sr;

    if (
      selectedPublicRequest.value &&
      selectedPublicRequest.value.id === selectedRequestId.value
    )
      return selectedPublicRequest.value;

    return null;
  });

  const getFormValues = (): {
    title: string;
    description: EditorValue;
    priority: Priority | undefined;
    dueDate: string | null;
    relationships: Array<Relationship>;
    customFieldValues: Record<
      CustomField['id'],
      CustomFields['value'] | undefined
    >;
  } => ({
    title: '',
    description: {
      html: '<p></p>',
    },
    priority: undefined,
    dueDate: null,
    relationships: [],
    customFieldValues: {},
  });
  const formValues = ref<ReturnType<typeof getFormValues>>(getFormValues());

  const populateFormValues = () => {
    if (!selectedRequest.value) return;
    selectedRequest.value.components.forEach((c) => {
      if (c.type !== 'field') return;
      if (c.subtype === 'itemField') {
        if (c.field === 'title')
          formValues.value.title = cloneDeep(c.defaultValue as string);
        if (c.field === 'description')
          formValues.value.description = cloneDeep(
            c.defaultValue as { html: string }
          );
        if (c.field === 'priorityId') {
          const priority = priorities.value?.find(
            (p) => p.id === c.defaultValue
          );
          formValues.value.priority = cloneDeep(priority);
        }
        if (c.field === 'dueDate')
          formValues.value.dueDate = cloneDeep(c.defaultValue as string);
        if (c.field === 'relationships')
          formValues.value.relationships = cloneDeep(
            c.defaultValue as Relationship[]
          );
      } else if (c.subtype === 'customField') {
        formValues.value.customFieldValues[c.field] = cloneDeep(
          c.defaultValue as CustomFields['value']
        );
      }
    });
  };

  const setSelectedRequestId = async (idOrNull: ItemRequest['id'] | null) => {
    selectedRequestId.value = idOrNull;
    formValues.value = getFormValues(); // reset form values
    if (idOrNull) {
      await nextTick();
      populateFormValues();
    } else {
      formValues.value = getFormValues(); // reset form values
    }
  };

  const setSelectedPublicRequest = async (sr: ItemRequest | null) => {
    if (sr) {
      selectedPublicRequest.value = sr;
      selectedRequestId.value = sr.id;
      formValues.value = getFormValues(); // reset form values
      await nextTick();
      populateFormValues();
    } else {
      selectedPublicRequest.value = null;
      selectedRequestId.value = null;
      formValues.value = getFormValues(); // reset form values
    }
  };

  const { customFields } = useCustomFields({
    workTypeIds: computed(() =>
      selectedRequest.value ? [selectedRequest.value.workTypeId] : []
    ),
  });
  const fields = computed(() => {
    if (!selectedRequest.value)
      return { fields: [], customFieldMap: {}, priorityOptions: [] };
    const formFields = selectedRequest.value.components.filter((c) => {
      if (c.type !== 'heading' && c.hidden) return false;

      // filter out user type custom fields in public requests (though this shouldn't happen in the first place, just a safeguard)
      if (
        selectedRequest.value?.public &&
        c.type === 'field' &&
        c.subtype === 'customField'
      ) {
        return c.customField && c.customField.type !== CustomFieldType.ENTITY;
      }
      return true;
    });
    const customFieldMap: Record<CustomField['id'], CustomField> = {};
    formFields.forEach((field) => {
      if (field.type !== 'field' || field.subtype !== 'customField') return;
      const cField =
        field.customField ??
        customFields.value?.find((cf) => cf.id === field.field) ??
        undefined;
      if (!cField) return;
      customFieldMap[cField.id] = cField;
    });

    const priorityOptions: Priority[] = [];
    formFields.forEach((field) => {
      if (field.type !== 'field' || field.subtype !== 'itemField') return;
      if (field.field !== 'priorityId') return;
      if (!field.options) return;
      priorityOptions.push(
        ...field.options.map((o) => {
          const priority: Priority = {
            organizationId: selectedRequest.value?.organizationId ?? '',
            id: o.id,
            name: o.label,
            color: o.color as Colors,
            description: '',
            dateAdded: new Date().toISOString(),
            dateUpdated: new Date().toISOString(),
          };
          return priority;
        })
      );
    });

    return {
      fields: formFields,
      customFieldMap,
      priorityOptions,
    };
  });

  const isFormValid = computed(() => {
    const { fields: formFields } = fields.value;
    let message = '';
    const valid = formFields.every((field) => {
      if (field.type !== 'field') return true;
      if (field.subtype === 'itemField') {
        // title
        if (field.field === 'title') {
          if (!formValues.value.title) {
            message = 'Title is required';
            return false;
          }
        }

        // due date
        if (field.field === 'dueDate' && field.required) {
          if (!formValues.value.dueDate) {
            message = 'Due date is required';
            return false;
          }
        }

        // relationships
        if (field.field === 'relationships' && field.required) {
          if (!formValues.value.relationships.length) {
            message = 'Relationships are required';
            return false;
          }
        }

        // description
        if (field.field === 'description' && field.required) {
          if (!formValues.value.description.html) {
            message = 'Description is required';
            return false;
          }
        }

        // priority
        if (field.field === 'priorityId' && field.required) {
          if (!formValues.value.priority) {
            message = 'Priority is required';
            return false;
          }
        }
      }
      // custom field
      else if (field.subtype === 'customField') {
        if (field.required) {
          const cField = fields.value.customFieldMap[field.field];
          if (!cField) {
            message = 'Custom field not supported. Contact support.';
            return false;
          }
          if (!formValues.value.customFieldValues[cField.id]) {
            message = `${cField.name} is required`;
            return false;
          }
        }
      }

      return true;
    });
    return {
      valid,
      message,
    };
  });

  const isSubmitting = ref(false);
  const handleSubmit = async () => {
    if (!isFormValid.value.valid) return;
    if (!selectedRequest.value) return;

    isSubmitting.value = true;

    const payload: SubmitItemRequest = {
      title: formValues.value.title,
    };

    const hasDescriptionField = fields.value.fields.some(
      (f) =>
        f.type === 'field' &&
        f.subtype === 'itemField' &&
        f.field === 'description'
    );
    if (hasDescriptionField) {
      const nextVal: EditorValue = isEqual(formValues.value.description, {
        html: '',
      })
        ? { html: '<p></p>' }
        : formValues.value.description;
      payload.description = nextVal;
    }
    if (formValues.value.priority) {
      payload.priorityId = formValues.value.priority.id;
    }
    if (formValues.value.dueDate) {
      payload.dueDate = formValues.value.dueDate;
    }
    if (formValues.value.relationships.length) {
      payload.relationships = formValues.value.relationships;
    }
    if (Object.keys(formValues.value.customFieldValues).length) {
      payload.customFields = {};
      for (const [cfId, cfValue] of Object.entries(
        formValues.value.customFieldValues
      )) {
        if (!cfValue) continue;
        if (
          typeof cfValue === 'string' ||
          typeof cfValue === 'number' ||
          typeof cfValue === 'boolean' ||
          Array.isArray(cfValue)
        ) {
          payload.customFields[cfId] = cfValue;
        } else {
          payload.customFields[cfId] = null;
        }
      }
    }

    try {
      if (selectedRequest.value.public) {
        const res = await root.$store.organization.submitPublicItemRequest(
          selectedRequest.value.organizationId,
          selectedRequest.value.id,
          payload
        );
        if (res?.success) {
          root.$notify({
            type: 'success',
            message:
              'Item request submitted successfully! You can now close this page.',
            timeout: 15 * 1000,
          });
        } else {
          throw new Error('Failed to create item request with success: false');
        }
      } else {
        const workItem = await root.$store.organization.submitItemRequest(
          selectedRequest.value.id,
          payload
        );
        // TODO: for private requests, have a flow to optionally open the created item
        if (workItem) {
          root.$notify({
            type: 'success',
            message: `Item request submitted successfully with work item: ${workItem.number}`,
            timeout: 10 * 1000,
          });
        } else {
          throw new Error('Failed to create item request');
        }
      }
      formValues.value = getFormValues(); // reset form values
    } catch (e) {
      console.error('Failed to create item request', e);
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    setSelectedRequestId,
    setSelectedPublicRequest,
    selectedRequest,
    formValues,
    fields,
    isFormValid,
    isSubmitting,
    handleSubmit,
  };
}
